import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchProducts } from '../lib/cms';
import Lightbox from '../components/Lightbox';

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Gallery state
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    async function loadData() {
      const data = await fetchProducts();
      setAllProducts(data);
      
      const found = data.find(p => p.slug === slug);
      if (found) {
        setProduct(found);
        setActiveImageIndex(0);
      } else {
        // If not found, redirect to 404
        navigate('/404');
      }
      setLoading(false);
    }
    loadData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-oak" />
      </div>
    );
  }

  if (!product) return null;

  // Combine product images and gallery for a full list of images
  const allImages = [
    ...(product.product_images || []),
    ...(product.gallery || [])
  ].filter(Boolean);

  // Get related products based on comma-separated slugs
  const relatedSlugs = product.related_products
    ? product.related_products.split(',').map(s => s.trim())
    : [];
  const relatedProducts = allProducts.filter(p => relatedSlugs.includes(p.slug));

  // Get inspiration products based on comma-separated slugs
  const inspirationSlugs = product.inspiration_products
    ? product.inspiration_products.split(',').map(s => s.trim())
    : [];
  const inspirationProducts = allProducts.filter(p => inspirationSlugs.includes(p.slug));

  return (
    <div className="py-16 bg-black animate-fade-in">
      <Helmet>
        <title>{`${product.name} – Heine Design`}</title>
        <meta name="description" content={product.short_description?.replace(/<[^>]*>/g, '') || ''} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-xs text-brand-charcoal uppercase tracking-widest mb-10">
          <Link to="/" className="hover:text-brand-oak transition-colors">Home</Link>
          <span>/</span>
          <Link to="/produkter" className="hover:text-brand-oak transition-colors">Furniture</Link>
          <span>/</span>
          <span className="text-white font-medium">{product.name}</span>
        </div>

        {/* Main Product Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-24">
          {/* Left: Gallery (lg:col-span-7) */}
          <div className="lg:col-span-7 space-y-4">
            {/* Main Image */}
            <div
              onClick={() => setIsLightboxOpen(true)}
              className="aspect-square bg-brand-light border border-brand-gray rounded-sm overflow-hidden cursor-zoom-in relative group"
            >
              <img
                src={allImages[activeImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass-plus" />
                <span>Klik for stor visning</span>
              </div>
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-5 gap-4">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`aspect-square bg-brand-light border rounded-sm overflow-hidden transition-all duration-300 ${
                      activeImageIndex === idx
                        ? 'border-brand-oak ring-1 ring-brand-oak/30'
                        : 'border-brand-gray hover:border-brand-oak'
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Info & Contact (lg:col-span-5) */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-3">
              <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                {product.terms?.['product-type']?.[0]?.replace('-', ' ')}
              </span>
              <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white uppercase">
                {product.name}
              </h1>
              <p className="text-xl font-semibold text-white pt-2">
                {product.price}
              </p>
            </div>

            {/* Short Description */}
            {product.short_description && (
              <div
                className="text-sm text-brand-charcoal leading-relaxed font-light border-t border-brand-gray pt-6"
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            )}

            {/* Materials & Specifications */}
            <div className="space-y-4 border-t border-brand-gray pt-6">
              <h3 className="text-xs tracking-[0.25em] uppercase text-white font-semibold">
                Specifikationer
              </h3>
              <ul className="space-y-2 text-xs text-brand-charcoal">
                {product.terms?.['product-material'] && (
                  <li className="flex justify-between py-1 border-b border-brand-gray">
                    <span className="font-medium">Materialer:</span>
                    <span className="capitalize">{product.terms['product-material'].join(', ').replace(/-/g, ' ')}</span>
                  </li>
                )}
                {product.terms?.['product-category'] && (
                  <li className="flex justify-between py-1 border-b border-brand-gray">
                    <span className="font-medium">Kategori:</span>
                    <span className="capitalize">{product.terms['product-category'].join(', ').replace(/-/g, ' ')}</span>
                  </li>
                )}
                <li className="flex justify-between py-1 border-b border-brand-gray">
                  <span className="font-medium">Håndværk:</span>
                  <span>100% Håndlavet i Danmark</span>
                </li>
              </ul>
            </div>

            {/* Contact Card & Forhandlere */}
            <div className="bg-brand-light border border-brand-gray rounded-sm p-6 space-y-6">
              <div className="space-y-2">
                <h4 className="text-xs tracking-[0.2em] uppercase text-white font-semibold">
                  Kontakt for pris og information
                </h4>
                <p className="text-xs text-brand-charcoal leading-relaxed">
                  Alle vores møbler produceres efter ordre og kan tilpasses dine specifikke mål og ønsker.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:+4522443585"
                  className="flex items-center justify-center gap-2 py-3 bg-brand-red text-white text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase hover:bg-brand-red-dark transition-colors duration-300 rounded-none text-center"
                >
                  <i className="fa-solid fa-phone" />
                  <span className="truncate">Ring +45 22 44</span>
                </a>
                <a
                  href={`mailto:info@heinedesign.com?subject=Forespørgsel: ${product.name}`}
                  className="flex items-center justify-center gap-2 py-3 border border-white text-white text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all duration-300 rounded-none text-center"
                >
                  <i className="fa-solid fa-envelope" />
                  <span className="truncate">Send E-mail</span>
                </a>
              </div>

              {/* Forhandlere Text Block (Elegantly integrated) */}
              <div className="border-t border-brand-gray pt-4 text-center">
                <p className="text-[11px] text-brand-charcoal italic">
                  For nærmeste forhandler kontakt mig på <a href="mailto:info@heinedesign.com" className="text-white hover:text-brand-oak hover:underline font-medium">info@heinedesign.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Long Description */}
        {product.long_description && (
          <div className="border-t border-brand-gray pt-12 mb-24">
            <h2 className="text-xs tracking-[0.25em] uppercase text-white font-semibold mb-6">
              Beskrivelse & Detaljer
            </h2>
            <div
              className="text-sm text-brand-charcoal leading-relaxed font-light max-w-4xl space-y-4"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        )}

        {/* Inspiration Products (Sidebar/Carousel) */}
        {inspirationProducts.length > 0 && (
          <div className="border-t border-brand-gray pt-12 mb-24">
            <div className="space-y-2 mb-8">
              <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                Inspiration
              </span>
              <h2 className="text-xl font-light tracking-widest text-white uppercase">
                Andre har også kigget på
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {inspirationProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/produkter/${p.slug}`}
                  className="group flex flex-col bg-brand-light border border-brand-gray rounded-sm overflow-hidden transition-all duration-300 hover:shadow-black/40 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="aspect-square overflow-hidden bg-black">
                    <img
                      src={p.product_images?.[0] || p.gallery?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="text-xs tracking-wider uppercase font-medium text-white group-hover:text-brand-oak transition-colors duration-300 line-clamp-1">
                      {p.name}
                    </h3>
                    <p className="text-xs font-semibold text-white">
                      {p.price}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="border-t border-brand-gray pt-12">
            <div className="space-y-2 mb-8">
              <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                Relateret design
              </span>
              <h2 className="text-xl font-light tracking-widest text-white uppercase">
                Relaterede Produkter
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {relatedProducts.map((p) => (
                <Link
                  key={p.id}
                  to={`/produkter/${p.slug}`}
                  className="group flex flex-col bg-brand-light border border-brand-gray rounded-sm overflow-hidden transition-all duration-300 hover:shadow-black/40 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="aspect-square overflow-hidden bg-black">
                    <img
                      src={p.product_images?.[0] || p.gallery?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="text-xs tracking-wider uppercase font-medium text-white group-hover:text-brand-oak transition-colors duration-300 line-clamp-1">
                      {p.name}
                    </h3>
                    <p className="text-xs font-semibold text-white">
                      {p.price}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <Lightbox
          images={allImages}
          currentIndex={activeImageIndex}
          onClose={() => setIsLightboxOpen(false)}
          onPrev={() => setActiveImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)}
          onNext={() => setActiveImageIndex((prev) => (prev + 1) % allImages.length)}
        />
      )}
    </div>
  );
}
